import React, {
  useEffect,
  //  useMemo,
  useState
} from 'react'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
// import { IFilterField } from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import ColorBox from 'common/Box/ColorWhiteBox'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import format from 'date-fns/format'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'
import { DataRolloverRows } from 'types/api/faber/subscription.type'

import FaberRolloverActionMenu from './components/FaberRolloverActionMenu'

const titles = [
  'Total Customers',
  'Lite Pac Active',
  'Pro Pac Active',
  'Tourist',
  'Backpacker',
  'Wanderer',
  'Jetsetter',
  'Globetrotter'
]
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
// import { CustomWinbackToolbar } from 'common/AbstractDataGridToolbar/CustomWinbackToolbar'
// import DateRangePicker from 'common/AbstractDateRangePicker/AbstractDateRangePicker'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { GetAllGlobalUsersDetails } from 'types/api/faber/globalCustomers.type'
import { GetAllFaberUsersDetails } from 'types/api/faber/oneTimePack.type'

import {
  DisplayTotals,
  StatsCards
} from '../../../../common/AbstractStatsCards'

interface DashboardPropType {
  tabValue: string
}

interface ViewProfileProps {
  customerRow:
    | SubscriptionsTableRows
    | GetAllFaberUsersDetails
    | GetAllGlobalUsersDetails
}
export function ViewProfileButton({ customerRow }: ViewProfileProps) {
  const navigate = useNavigate()

  const handleViewProfilePage = () => {
    //for Otpack customers
    if ('_id' in customerRow) {
      if (customerRow.subscription) {
        navigate(
          //subscription users - will reroute back to subscription
          `/hq/manage-customers-faber/view-profile/${customerRow?._id}`,
          {
            state: customerRow
          }
        )
      } else {
        //otpack only
        navigate(`view-profile/${customerRow?._id}`, {
          state: customerRow
        })
      }
    } else if ('userID' in customerRow) {
      //subscription users
      navigate(`view-profile/${customerRow.userID}`, {
        state: customerRow
      })
    } else {
      navigate(`view-profile/${customerRow.user_id}`, {
        state: customerRow
      })
    }
  }

  return (
    <>
      <Button
        sx={{
          backgroundColor: circlesOrbitColorPalette.primary.disabled,
          borderColor: circlesOrbitColorPalette.text.light,
          borderWidth: 0.5,
          ':hover': {
            backgroundColor: circlesOrbitColorPalette.primary.dark
          }
        }}
        onClick={handleViewProfilePage}
      >
        <ManageAccountsIcon
          sx={{ color: circlesOrbitColorPalette.neutrals[0] }}
        />
      </Button>
    </>
  )
}

export default function FaberCustomerDashboard(
  dashboardType: DashboardPropType
) {
  const [rows, setRows] = useState<SubscriptionsTableRows[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [
    statusChecked
    // setStatusChecked
  ] = useState<string[]>([])
  const [
    bundleChecked
    // setBundleChecked
  ] = useState<string[]>([])
  // const [filterActive, setFilterActive] = useState<boolean>(false)
  const [totals, setTotals] = useState<DisplayTotals[]>([])
  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  // hard code the start date to before launch date
  const [
    startDate
    // setStartDate
  ] = useState<Date>(new Date(2022, 9, 1))
  const [
    endDate
    // setEndDate
  ] = useState<Date>(changeMonth(new Date(), +3))
  const [sortDate, setSortDate] = useState<string>('DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')

  const [pageOffset, setPageOffset] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [rowCount, setRowCount] = useState(0)

  const role = OrbitSession.get('role')
  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Email', 'MSISDN', 'ICCID', 'Customer ID']

  const { tabValue } = dashboardType

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset)
    setPage(newPage)
  }

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    // CONVERT + TO %2B FOR QUERY URL
    setTempSearchValue(e.target.value.replace('+', '%2B'))
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  useEffect(() => {
    resetPage()
  }, [startDate, endDate])

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      filterStartDate: startDate,
      filterEndDate: endDate,
      sortDate
    }),
    [
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      startDate,
      endDate,
      sortDate
    ]
  )

  const { data: getAllSubscriptions, isLoading: gettingAllSubscriptions } =
    useQuery(
      ['getAllSubscriptions', [queryOptions]],
      async () => {
        return SubscriptionsAPI.getAllSubscriptions(
          faberToken,
          queryOptions.pageSize,
          queryOptions.pageOffset,
          queryOptions.statusChecked,
          queryOptions.bundleChecked,
          queryOptions.searchCriteria,
          queryOptions.searchValue,
          sortDate,
          format(queryOptions.filterStartDate, 'yyyy-MM-dd'),
          format(queryOptions.filterEndDate, 'yyyy-MM-dd')
        )
      },
      {
        onError: () => {
          toast.error('Error getting customer details')
          setRows([])
          setIsLoading(false)
        }
      }
    )

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel[0].sort === 'asc') {
        setSortDate('ASC')
      } else {
        setSortDate('DESC')
      }
    },
    []
  )

  const { data: getSubscriptionTotals } = useQuery(
    ['getSubscriptionTotals'],
    async () => {
      return SubscriptionsAPI.getSubscriptionTotals(faberToken)
    },
    {
      onError: () => {
        toast.error('Error getting subscription totals')
        setRows([])
        setIsLoading(false)
      }
    }
  )

  useEffect(() => {
    if (getAllSubscriptions && getAllSubscriptions.data && getAllSubscriptions.data.body) {
      const { data, total } = getAllSubscriptions.data.body
      const allSubscriptions: SubscriptionsTableRows[] = data.map(
        ({
          firstName,
          lastName,
          customerId,
          userEmail,
          msisdn,
          iccid,
          plan,
          status,
          startAt,
          cycleCount
        }) => ({
          firstName,
          lastName,
          userID: customerId,
          userEmail,
          iccid,
          planStatus: status,
          planName: plan,
          subscriptionStartAt: startAt,
          msisdn,
          cycleCount: String(cycleCount)
        })
      )
      setRowCount(total)
      setRows(allSubscriptions)
      setIsLoading(false)
    }
  }, [getAllSubscriptions])

  useEffect(() => {
    if (getSubscriptionTotals && getSubscriptionTotals.data && getSubscriptionTotals.data.data) {
      const { breakdown, total } = getSubscriptionTotals.data.data

      let totalLiteCount = 0
      let totalProCount = 0
      let totalTouristCount = 0
      let totalJetsetterCount = 0
      let totalWandererCount = 0
      let totalBackpackerCount = 0
      let totalGlobetrotterCount = 0

      for (let i = 0; i < breakdown.length; i++) {
        if (breakdown[i].name === 'Pro Pac') {
          totalProCount += breakdown[i].count
        }
        if (breakdown[i].name === 'Lite Pac') {
          totalLiteCount += breakdown[i].count
        }
        if (breakdown[i].bundleID === 106) {
          totalTouristCount += breakdown[i].count
        }
        if (breakdown[i].bundleID === 107) {
          totalBackpackerCount += breakdown[i].count
        }
        if (breakdown[i].bundleID === 108) {
          totalWandererCount += breakdown[i].count
        }
        if (breakdown[i].bundleID === 109) {
          totalJetsetterCount += breakdown[i].count
        }
        if (breakdown[i].bundleID === 110) {
          totalGlobetrotterCount += breakdown[i].count
        }
      }

      const summary: DisplayTotals[] = [
        {
          title: titles[0],
          data: [String(total)]
        },
        {
          title: titles[1],
          data: [String(totalLiteCount)]
        },
        {
          title: titles[2],
          data: [String(totalProCount)]
        },
        {
          title: titles[3],
          data: [String(totalTouristCount)]
        },
        {
          title: titles[4],
          data: [String(totalBackpackerCount)]
        },
        {
          title: titles[5],
          data: [String(totalWandererCount)]
        },
        {
          title: titles[6],
          data: [String(totalJetsetterCount)]
        },
        {
          title: titles[7],
          data: [String(totalGlobetrotterCount)]
        }
      ]
      setTotals(summary)
    } else {
      setTotals([])
    }
  }, [getSubscriptionTotals])

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }
  const columns: GridColDef[] =
    tabValue === '1'
      ? [
          {
            field: 'subscriptionStartAt',
            sortable: true,
            headerName: 'Start Date',
            renderCell: (params) => {
              const subscriptionDate = new Date(
                `${params.row.subscriptionStartAt}`
              )
              return (
                <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                  {format(subscriptionDate, 'dd MMM yyyy')}
                </Typography>
              )
            },
            flex: 0.75,
            minWidth: 100
          },

          {
            field: 'firstName',
            sortable: false,
            headerName: 'Name',
            flex: 1,
            width: 100,
            renderCell: (params: { row: SubscriptionsTableRows }) => {
              return (
                <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                  {params.row.firstName} {params.row.lastName}
                </Typography>
              )
            }
          },
          {
            field: 'userID',
            sortable: false,
            headerName: 'Customer ID',
            flex: 1.5,
            width: 100,
            renderCell: (params: { row: SubscriptionsTableRows }) => {
              return (
                <Tooltip title={params.row.userID} placement='top'>
                  <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                    {params.row.userID}
                  </Typography>
                </Tooltip>
              )
            }
          },
          {
            field: 'msisdn',
            sortable: false,
            headerName: 'MSISDN',
            flex: 1,
            width: 100
          },
          {
            field: 'userEmail',
            sortable: false,
            headerName: 'Email',
            flex: 2,
            minWidth: 150,
            renderCell: (params: { row: SubscriptionsTableRows }) => {
              return (
                <Tooltip title={params.row.userEmail} placement='top'>
                  <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                    {params.row.userEmail}
                  </Typography>
                </Tooltip>
              )
            }
          },
          {
            field: 'iccid',
            sortable: false,
            headerName: 'ICCID',
            flex: 1.25,
            minWidth: 100
          },
          {
            field: 'planStatus',
            sortable: false,
            headerName: 'Status',
            flex: 0.75,
            minWidth: 50,
            renderCell: (params: { row: SubscriptionsTableRows }) => {
              if (params.row.planStatus === 'TERMINATED') {
                return (
                  <Tooltip title='UNSUBSCRIBED' placement='top'>
                    <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                      UNSUBSCRIBED
                    </Typography>
                  </Tooltip>
                )
              } else if (params.row.planStatus === 'EXPIRING') {
                return (
                  <Tooltip title='PENDING UNSUBSCRIPTION' placement='top'>
                    <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                      PENDING UNSUBSCRIPTION
                    </Typography>
                  </Tooltip>
                )
              } else {
                return (
                  <Tooltip title={params.row.planStatus} placement='top'>
                    <Typography sx={{ fontSize: '12px', color: '#253140' }}>
                      {params.row.planStatus}
                    </Typography>
                  </Tooltip>
                )
              }
            }
          },
          {
            field: 'planName',
            sortable: false,
            headerName: 'Plan',
            flex: 0.5,
            minWidth: 50
          },
          {
            field: 'cycleCount',
            headerName: 'Cycle Count',
            minWidth: 100
          },
          {
            field: 'viewProfile',
            flex: 0.75,
            minWidth: 90,
            filterable: false,
            sortable: false,
            headerName: 'View Profile',
            renderCell: (params) => {
              return role !== 'opsviewer' ? (
                <ViewProfileButton customerRow={params.row} />
              ) : (
                ''
              )
            }
          }
        ]
      : // if next tab then show other column fields
        [
          {
            field: 'subscriptionStartAt',
            headerName: 'Start Date',
            renderCell: (params: { row: DataRolloverRows }) => {
              const date = new Date(`${params.row.subscriptionStartAt}`)
              return date.toLocaleDateString('en-GB')
            },
            minWidth: 100
          },
          { field: 'firstName', headerName: 'Name', width: 100 },
          { field: 'userID', headerName: 'Customer ID', width: 100 },
          { field: 'msisdn', headerName: 'MSISDN', width: 100 },
          {
            field: 'userEmail',
            headerName: 'Email',
            width: 100
          },
          { field: 'iccid', headerName: 'ICC ID', width: 100 },
          {
            field: 'planStatus',
            headerName: 'Status',
            width: 100
          },
          {
            field: 'planName',
            headerName: 'Plan',
            width: 100
          },
          {
            field: 'cycleCount',
            headerName: 'Cycle Count',
            minWidth: 100
          },

          {
            field: 'actions',
            headerName: 'Actions',
            minWidth: 100,
            filterable: false,
            renderCell: (params: { row: DataRolloverRows }) => {
              return <FaberRolloverActionMenu row={params.row} />
            }
          }
        ]

  // const filterFields = useMemo(() => {
  //   let tempFilterFields: IFilterField[] = []
  //   tempFilterFields = [
  //     {
  //       values: ['ACTIVE', 'CANCELLED', 'PENDING UNSUBSCRIPTION', 'SUSPENDED'],
  //       checked: statusChecked,
  //       setChecked: setStatusChecked,
  //       name: 'Status',
  //       index: 'status'
  //     },
  //     {
  //       values: ['Lite Pac', 'Pro Pac'],
  //       checked: bundleChecked,
  //       setChecked: setBundleChecked,
  //       name: 'Bundle',
  //       index: 'bundleName'
  //     }
  //   ]

  //   return tempFilterFields
  // }, [statusChecked, bundleChecked])

  return !isLoading ? (
    <Box>
      <StatsCards
        totals={totals}
        icon={<SentimentSatisfiedAltIcon color='primary' />}
      />
      <ColorBox>
        <Box>
          <MUIDataGrid
            getRowId={(row) => `${row.iccid}-${rows.indexOf(row)}`}
            tableHeight='750px'
            rows={rows}
            pagination
            columns={columns}
            paginationMode='server'
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            disableColumnMenu={true}
            sortingMode='server'
            sortingOrder={['desc', 'asc']}
            onSortModelChange={handleSortModelChange}
            loading={gettingAllSubscriptions}
            components={{
              Toolbar: Toolbar
            }}
            componentsProps={{
              toolbar: {
                // serverFilterProps: {
                //   filterFields,
                //   filterActive,
                //   setFilterActive
                // },
                searchBar: 'server',
                serverSearchBarProps: {
                  onSubmit: onSearchSubmit,
                  onChange: onSearchValueChange,
                  options: searchOptions,
                  defaultValue: searchCriteria,
                  handleChange: handleSearchCriteriaChange
                }
                // dateRangePicker: (
                //   <DateRangePicker
                //     startDate={startDate}
                //     setStartDate={setStartDate}
                //     endDate={endDate}
                //     setEndDate={setEndDate}
                //   />
                // ),
                // filter: 'server'
              }
            }}
          />
        </Box>
      </ColorBox>
    </Box>
  ) : (
    <CircularProgress />
  )
}
